import { createContext, useContext } from 'react';

export const DeviceTypeContext = createContext('desktop');

export function useDeviceTypeContext() {
    return useContext(DeviceTypeContext);
}

/**
 * Server-side device type detection based on User-Agent and screen size
 * @param {string} userAgent - User-Agent string from request headers
 * @param {Object} headers - Request headers object (optional)
 * @returns {string} Device type: 'mobile', 'tablet-2-columns', 'tablet-3-columns', or 'desktop'
 */
export function getDeviceType(userAgent, headers = null) {
    if (!userAgent) {
        return 'desktop';
    }

    const ua = userAgent.toLowerCase();

    if (
        (ua.includes('android') && ua.includes('mobile')) ||
        ua.includes('iphone') ||
        ua.includes('ipod') ||
        ua.includes('blackberry') ||
        ua.includes('windows phone') ||
        ua.includes('opera mini')
    ) {
        return 'mobile';
    }

    // 1440px - 980px =  tablet-3-columns
    // 980px - 655px = tablet-2-columns
    if (
        ua.includes('tablet') ||
        ua.includes('ipad') ||
        ua.includes('kindle') ||
        ua.includes('silk') ||
        ua.includes('playbook') ||
        (ua.includes('android') && !ua.includes('mobile'))
    ) {
        const screenWidth = getScreenWidthFromHeaders(headers);

        if (screenWidth) {
            if (screenWidth >= 980 && screenWidth <= 1440) {
                return 'tablet-3-columns';
            } else if (screenWidth >= 655 && screenWidth < 980) {
                return 'tablet-2-columns';
            }
        }

        return 'tablet-3-columns';
    }
    return 'desktop';
}

/**
 * Extract screen width from request headers
 * @param {Object} headers - Request headers object
 * @returns {number|null} Screen width in pixels or null if not available
 */
function getScreenWidthFromHeaders(headers) {
    if (!headers) return null;

    try {
        const screenWidth =
            headers.get('sec-ch-viewport-width') ||
            headers.get('viewport-width') ||
            headers.get('screen-width') ||
            headers.get('device-width');

        if (screenWidth) {
            const width = parseInt(screenWidth, 10);
            return isNaN(width) ? null : width;
        }

        const userAgent = headers.get('user-agent') || '';
        return getScreenWidthFromUserAgent(userAgent);
    } catch (error) {
        console.warn('Error extracting screen width from headers:', error);
        return null;
    }
}

/**
 * Extract screen width from User-Agent for known devices
 * @param {string} userAgent - User-Agent string
 * @returns {number|null} Screen width in pixels or null if not available
 */
function getScreenWidthFromUserAgent(userAgent) {
    const ua = userAgent.toLowerCase();

    if (ua.includes('ipad')) {
        if (ua.includes('pro')) {
            return 1024;
        }
        return 768;
    }

    if (ua.includes('android') && !ua.includes('mobile')) {
        return 1024;
    }

    if (ua.includes('kindle') || ua.includes('silk')) {
        return 800;
    }

    return null;
}

/**
 * Get device type from browser APIs (client-side only).
 * Safe to call in useEffect; returns 'desktop' in SSR context.
 *
 * The fakeHeaders shim below passes window.innerWidth as `sec-ch-viewport-width`
 * so that `getDeviceType` can use it for tablet sub-classification on Chrome
 * (where the header hint is reliable). On Safari/Firefox the header key will
 * return a value from this shim but the logic still works because we're
 * explicitly supplying `window.innerWidth` ourselves — it is NOT relying on the
 * browser sending the real client-hint header. UA-based tablet detection then
 * uses this width for the 655/980/1440px breakpoint logic.
 * @returns {string} Device type
 */
export function getDeviceTypeFromWindow() {
    if (typeof window === 'undefined') return 'desktop';

    const fakeHeaders = {
        get(key) {
            if (key === 'sec-ch-viewport-width' || key === 'viewport-width') {
                return String(window.innerWidth);
            }
            return null;
        }
    };

    return getDeviceType(navigator.userAgent, fakeHeaders);
}
